/**
 * Cyclespot Seeker / Pursuit cart compatibility bridge.
 *
 * Seeker updates Shopify's underlying Ajax cart, while Pursuit maintains its
 * own cart drawer markup and header count. This bridge observes successful
 * Seeker cart mutations, then invokes Pursuit's native cart refresh/open flow.
 *
 * It deliberately does not change Seeker's quantity semantics. Seeker's
 * /cart/update.js request treats the quantity picker as the desired final
 * quantity, avoiding accidental increments from repeated taps.
 */
(function () {
  'use strict';

  if (window.CyclespotSeekerCartBridge) return;

  var HOST_SELECTOR = 'seeker-dropdown-parts-finder-root';
  var INTERACTION_WINDOW_MS = 10000;
  var DRAWER_FALLBACK_DELAY_MS = 4000;
  var REQUEST_WATCHDOG_MS = 15000;

  var nativeFetch = typeof window.fetch === 'function'
    ? window.fetch.bind(window)
    : null;

  var state = {
    lastInteractionAt: 0,
    activeControl: null,
    activeControlLooksLikeCart: false,
    controlSnapshot: null,
    pendingRequests: 0,
    feedbackHideTimer: null,
    controlRestoreTimer: null,
    requestWatchdogTimer: null,
    lastPursuitRefreshAt: 0
  };

  function now() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  function isElement(node) {
    return !!node && node.nodeType === 1;
  }

  function matches(node, selector) {
    if (!isElement(node)) return false;
    var fn = node.matches || node.msMatchesSelector || node.webkitMatchesSelector;
    return !!fn && fn.call(node, selector);
  }

  function getEventPath(event) {
    if (event.composedPath) return event.composedPath();

    var path = [];
    var node = event.target;
    while (node) {
      path.push(node);
      node = node.parentNode || node.host;
    }
    path.push(window);
    return path;
  }

  function pathContainsSeeker(path) {
    for (var i = 0; i < path.length; i += 1) {
      if (matches(path[i], HOST_SELECTOR)) return true;
    }
    return false;
  }

  function findInteractiveControl(path) {
    var selector = 'button, a, input[type="button"], input[type="submit"], [role="button"]';

    for (var i = 0; i < path.length; i += 1) {
      if (matches(path[i], HOST_SELECTOR)) break;
      if (matches(path[i], selector)) return path[i];
    }

    return null;
  }

  function controlLooksLikeCartAction(control) {
    if (!control) return false;

    var signal = [
      control.textContent,
      control.value,
      control.getAttribute('aria-label'),
      control.getAttribute('title'),
      control.getAttribute('name'),
      control.getAttribute('id'),
      control.getAttribute('class'),
      control.getAttribute('data-action')
    ].join(' ').toLowerCase();

    return /add\s*(?:to\s*)?(?:cart|basket)|add[-_]?to[-_]?(?:cart|basket)|cart[-_]?add/.test(signal);
  }

  function rememberSeekerInteraction(event) {
    var path = getEventPath(event);
    if (!pathContainsSeeker(path)) return;

    state.lastInteractionAt = now();
    state.activeControl = findInteractiveControl(path);
    state.activeControlLooksLikeCart = controlLooksLikeCartAction(state.activeControl);
  }

  document.addEventListener('click', rememberSeekerInteraction, true);
  document.addEventListener('submit', rememberSeekerInteraction, true);

  function getRequestUrl(input) {
    if (typeof input === 'string') return input;
    if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function getRequestMethod(input, init) {
    var method = init && init.method;
    if (!method && input && typeof input.method === 'string') method = input.method;
    return String(method || 'GET').toUpperCase();
  }

  function getRequestBody(input, init) {
    if (init && Object.prototype.hasOwnProperty.call(init, 'body')) return init.body;
    return input && Object.prototype.hasOwnProperty.call(input, '_bodyInit')
      ? input._bodyInit
      : null;
  }

  function getCartEndpoint(url) {
    if (!url) return '';

    try {
      var pathname = new URL(url, window.location.href).pathname;
      return /\/cart\/update\.js$/i.test(pathname) ? 'update' : '';
    } catch (error) {
      return /\/cart\/update\.js(?:[?#]|$)/i.test(String(url)) ? 'update' : '';
    }
  }

  function bodyContainsUpdates(body) {
    if (!body) return false;

    if (typeof body === 'string') {
      var value = body;
      try {
        value += ' ' + decodeURIComponent(body.replace(/\+/g, '%20'));
      } catch (error) {
        // The original string is still sufficient for normal JSON/form bodies.
      }
      return /(?:^|[\s&?{,\"'])updates(?:%5B|\[|[\"']?\s*:|=)/i.test(value);
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var foundInFormData = false;
      body.forEach(function (unusedValue, key) {
        if (key === 'updates' || key.indexOf('updates[') === 0) {
          foundInFormData = true;
        }
      });
      return foundInFormData;
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      var foundInParams = false;
      body.forEach(function (unusedValue, key) {
        if (key === 'updates' || key.indexOf('updates[') === 0) {
          foundInParams = true;
        }
      });
      return foundInParams;
    }

    return false;
  }

  function isRecentSeekerInteraction() {
    return now() - state.lastInteractionAt <= INTERACTION_WINDOW_MS;
  }

  function shouldTrackRequest(url, method, body) {
    if (method !== 'POST') return false;

    var endpoint = getCartEndpoint(url);
    if (!endpoint) return false;

    var seekerExists = !!document.querySelector(HOST_SELECTOR);
    if (!seekerExists && !isRecentSeekerInteraction()) return false;

    // Seeker submits an updates object to update.js. Requiring that signature
    // avoids reacting to Pursuit cart-note and cart-attribute requests, which
    // also use update.js. The recent-control fallback covers unusual Request or
    // Blob bodies that cannot be inspected synchronously.
    if (endpoint === 'update' && bodyContainsUpdates(body)) return true;

    return endpoint === 'update' &&
      isRecentSeekerInteraction() &&
      state.activeControlLooksLikeCart;
  }

  function injectFeedbackStyles() {
    if (document.getElementById('cyclespot-seeker-cart-bridge-styles')) return;

    var style = document.createElement('style');
    style.id = 'cyclespot-seeker-cart-bridge-styles';
    style.textContent = [
      '#cyclespot-seeker-cart-feedback{position:fixed;left:50%;bottom:calc(18px + env(safe-area-inset-bottom,0px));z-index:2147483000;display:flex;align-items:center;gap:10px;max-width:calc(100vw - 32px);padding:12px 16px;border-radius:3px;background:#1d1d1d;color:#fff;font:600 14px/1.35 Arial,sans-serif;box-shadow:0 8px 28px rgba(0,0,0,.28);opacity:0;visibility:hidden;pointer-events:none;transform:translate(-50%,14px);transition:opacity .18s ease,transform .18s ease,visibility .18s ease}',
      '#cyclespot-seeker-cart-feedback.is-visible{opacity:1;visibility:visible;transform:translate(-50%,0)}',
      '#cyclespot-seeker-cart-feedback[data-state="success"]{background:#237a3b}',
      '#cyclespot-seeker-cart-feedback[data-state="error"]{background:#a32622}',
      '#cyclespot-seeker-cart-feedback [data-seeker-feedback-icon]{display:inline-block;flex:0 0 auto;width:16px;height:16px;border:2px solid currentColor;border-radius:50%;box-sizing:border-box}',
      '#cyclespot-seeker-cart-feedback[data-state="loading"] [data-seeker-feedback-icon]{border-right-color:transparent;animation:cyclespotSeekerCartSpin .7s linear infinite}',
      '#cyclespot-seeker-cart-feedback[data-state="success"] [data-seeker-feedback-icon]{border:0;width:auto;height:auto;font-size:17px;line-height:1}',
      '#cyclespot-seeker-cart-feedback[data-state="error"] [data-seeker-feedback-icon]{border:0;width:auto;height:auto;font-size:17px;line-height:1}',
      '@keyframes cyclespotSeekerCartSpin{to{transform:rotate(360deg)}}',
      '@media (prefers-reduced-motion:reduce){#cyclespot-seeker-cart-feedback{transition:none}#cyclespot-seeker-cart-feedback[data-state="loading"] [data-seeker-feedback-icon]{animation:none}}'
    ].join('');

    (document.head || document.documentElement).appendChild(style);
  }

  function getFeedbackElement() {
    var feedback = document.getElementById('cyclespot-seeker-cart-feedback');
    if (feedback) return feedback;
    if (!document.body) return null;

    feedback = document.createElement('div');
    feedback.id = 'cyclespot-seeker-cart-feedback';
    feedback.setAttribute('role', 'status');
    feedback.setAttribute('aria-live', 'polite');
    feedback.setAttribute('aria-atomic', 'true');
    feedback.innerHTML = '<span data-seeker-feedback-icon aria-hidden="true"></span><span data-seeker-feedback-message></span>';
    document.body.appendChild(feedback);
    return feedback;
  }

  function showFeedback(feedbackState, message, hideAfterMs) {
    injectFeedbackStyles();

    var feedback = getFeedbackElement();
    if (!feedback) return;

    window.clearTimeout(state.feedbackHideTimer);
    feedback.setAttribute('data-state', feedbackState);
    feedback.setAttribute('role', feedbackState === 'error' ? 'alert' : 'status');
    feedback.setAttribute('aria-live', feedbackState === 'error' ? 'assertive' : 'polite');

    var icon = feedback.querySelector('[data-seeker-feedback-icon]');
    var messageElement = feedback.querySelector('[data-seeker-feedback-message]');
    if (icon) icon.textContent = feedbackState === 'success' ? '✓' : (feedbackState === 'error' ? '!' : '');
    if (messageElement) messageElement.textContent = message;

    feedback.classList.add('is-visible');

    if (hideAfterMs) {
      state.feedbackHideTimer = window.setTimeout(function () {
        feedback.classList.remove('is-visible');
      }, hideAfterMs);
    }
  }

  function captureControlSnapshot(control) {
    return {
      control: control,
      ariaBusy: control.getAttribute('aria-busy'),
      ariaDisabled: control.getAttribute('aria-disabled'),
      pointerEvents: control.style.pointerEvents,
      opacity: control.style.opacity,
      cursor: control.style.cursor,
      inputValue: control.tagName === 'INPUT' ? control.value : null
    };
  }

  function removeControlIndicator(control) {
    if (!control || !control.querySelector) return;
    var indicator = control.querySelector('[data-seeker-cart-bridge-indicator]');
    if (indicator && indicator.parentNode) indicator.parentNode.removeChild(indicator);
  }

  function setControlFeedback(feedbackState) {
    var control = state.activeControl;
    if (!control || !control.isConnected) return;

    if (!state.controlSnapshot || state.controlSnapshot.control !== control) {
      state.controlSnapshot = captureControlSnapshot(control);
    }

    window.clearTimeout(state.controlRestoreTimer);
    removeControlIndicator(control);

    control.setAttribute('aria-busy', feedbackState === 'loading' ? 'true' : 'false');
    control.setAttribute('aria-disabled', feedbackState === 'loading' ? 'true' : 'false');
    control.style.pointerEvents = feedbackState === 'loading' ? 'none' : state.controlSnapshot.pointerEvents;
    control.style.opacity = feedbackState === 'loading' ? '0.72' : state.controlSnapshot.opacity;
    control.style.cursor = feedbackState === 'loading' ? 'wait' : state.controlSnapshot.cursor;

    var label = feedbackState === 'loading' ? ' Adding…' : (feedbackState === 'success' ? ' Added' : ' Try again');

    if (control.tagName === 'INPUT') {
      control.value = label.replace(/^\s+/, '');
      return;
    }

    var indicator = document.createElement('span');
    indicator.setAttribute('data-seeker-cart-bridge-indicator', '');
    indicator.setAttribute('aria-hidden', 'true');
    indicator.textContent = label;
    indicator.style.cssText = 'display:inline-block;margin-left:7px;white-space:nowrap;font:inherit;color:inherit;';
    control.appendChild(indicator);
  }

  function restoreControl(delayMs) {
    window.clearTimeout(state.controlRestoreTimer);

    state.controlRestoreTimer = window.setTimeout(function () {
      var snapshot = state.controlSnapshot;
      if (!snapshot || !snapshot.control) {
        state.controlSnapshot = null;
        state.activeControl = null;
        state.activeControlLooksLikeCart = false;
        return;
      }

      var control = snapshot.control;
      if (control.isConnected) {
        removeControlIndicator(control);

        if (snapshot.ariaBusy === null) control.removeAttribute('aria-busy');
        else control.setAttribute('aria-busy', snapshot.ariaBusy);

        if (snapshot.ariaDisabled === null) control.removeAttribute('aria-disabled');
        else control.setAttribute('aria-disabled', snapshot.ariaDisabled);

        control.style.pointerEvents = snapshot.pointerEvents;
        control.style.opacity = snapshot.opacity;
        control.style.cursor = snapshot.cursor;

        if (control.tagName === 'INPUT' && snapshot.inputValue !== null) {
          control.value = snapshot.inputValue;
        }
      }

      state.controlSnapshot = null;
      state.activeControl = null;
      state.activeControlLooksLikeCart = false;
    }, delayMs || 0);
  }

  function beginTrackedRequest() {
    state.pendingRequests += 1;
    state.lastInteractionAt = 0;
    state.activeControlLooksLikeCart = false;
    setControlFeedback('loading');
    showFeedback('loading', 'Adding to cart…');

    window.clearTimeout(state.requestWatchdogTimer);
    state.requestWatchdogTimer = window.setTimeout(function () {
      if (state.pendingRequests > 0) {
        setControlFeedback('error');
        showFeedback('error', 'This is taking longer than expected. Please try again.', 5000);
        restoreControl(3000);
      }
    }, REQUEST_WATCHDOG_MS);
  }

  function finishTrackedRequest() {
    state.pendingRequests = Math.max(0, state.pendingRequests - 1);
    if (state.pendingRequests === 0) {
      window.clearTimeout(state.requestWatchdogTimer);
    }
  }

  function normaliseErrorMessage(payload) {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;

    var message = payload.description || payload.message || payload.error;
    if (typeof message === 'string') return message;

    if (message && typeof message === 'object') {
      for (var key in message) {
        if (Object.prototype.hasOwnProperty.call(message, key)) {
          if (Array.isArray(message[key])) return String(message[key][0] || '');
          return String(message[key] || '');
        }
      }
    }

    return '';
  }

  function updateHeaderFromCart(cart) {
    if (!cart) return;

    var rawItemCount = typeof cart.item_count !== 'undefined'
      ? cart.item_count
      : cart.itemCount;
    var rawTotalPrice = typeof cart.total_price !== 'undefined'
      ? cart.total_price
      : cart.totalPrice;

    if (typeof rawItemCount === 'undefined') return;

    var itemCount = Number(rawItemCount) || 0;
    var countElements = document.querySelectorAll('[data-cart-count], .header_cart_count');
    for (var i = 0; i < countElements.length; i += 1) {
      countElements[i].textContent = itemCount;
      if (itemCount > 0) {
        countElements[i].classList.remove('hide');
        countElements[i].classList.remove('shide');
      } else {
        countElements[i].classList.add('shide');
      }
    }

    var totalElement = document.querySelector('.cart_total_price');
    if (
      totalElement &&
      typeof rawTotalPrice !== 'undefined' &&
      window.theme &&
      window.theme.Currency &&
      typeof window.theme.Currency.formatMoney === 'function'
    ) {
      totalElement.innerHTML = window.theme.Currency.formatMoney(rawTotalPrice, window.theme.moneyFormat);
    }
  }

  function openDrawerManually() {
    var drawer = document.querySelector('#sidebar-cart');
    if (!drawer) return false;

    drawer.classList.add('show');
    if (document.body) document.body.classList.add('show_overlay');

    var focusApplied = false;
    var focusDrawer = function () {
      if (focusApplied) return;
      focusApplied = true;

      try {
        drawer.focus();
        if (
          window.slate &&
          window.slate.a11y &&
          typeof window.slate.a11y.trapFocus === 'function'
        ) {
          window.slate.a11y.trapFocus({
            container: drawer,
            elementToFocus: drawer
          });
        }
      } catch (error) {
        // The drawer is still usable even if optional focus helpers are absent.
      }
    };

    drawer.addEventListener('transitionend', focusDrawer, { once: true });
    window.setTimeout(focusDrawer, 350);
    return true;
  }

  function fallbackRefreshAndOpen(cart) {
    updateHeaderFromCart(cart);

    if (!nativeFetch) {
      openDrawerManually();
      return;
    }

    var cartUrl = window.routes && window.routes.cartUrl
      ? window.routes.cartUrl
      : '/cart';

    nativeFetch(cartUrl + '?view=drawer&timestamp=' + now(), {
      credentials: 'same-origin',
      method: 'GET'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to refresh cart drawer');
        return response.text();
      })
      .then(function (html) {
        var temp = document.createElement('div');
        temp.innerHTML = html;

        var currentDrawer = document.querySelector('#sidebar-cart');
        var freshDrawer = temp.querySelector('#sidebar-cart');
        var freshSettingsNode = temp.querySelector('[data-section-type="cart-template"]');

        if (currentDrawer && freshDrawer) {
          var currentCart = currentDrawer.querySelector('.Cart');
          var freshCart = freshDrawer.querySelector('.Cart');
          if (currentCart && freshCart && currentCart.parentNode) {
            currentCart.parentNode.replaceChild(freshCart, currentCart);
          }

          if (freshSettingsNode) {
            currentDrawer.setAttribute(
              'data-section-settings',
              freshSettingsNode.getAttribute('data-section-settings') || '{}'
            );

            try {
              updateHeaderFromCart(JSON.parse(freshSettingsNode.getAttribute('data-section-settings') || '{}'));
            } catch (error) {
              // Header was already updated from the Ajax cart response when available.
            }
          }
        }

        openDrawerManually();
      })
      .catch(function () {
        openDrawerManually();
      });
  }

  function invokePursuitCart(cart, attempt) {
    attempt = attempt || 0;

    if (
      window.theme &&
      window.theme.Cart &&
      window.theme.Cart.prototype &&
      typeof window.theme.Cart.prototype._onProductAdded === 'function'
    ) {
      try {
        window.theme.Cart.prototype._onProductAdded();

        // If a later theme/app update prevents the native drawer from opening,
        // retain a defensive fallback without disturbing the normal path.
        window.setTimeout(function () {
          var drawer = document.querySelector('#sidebar-cart');
          if (!drawer || !drawer.classList.contains('show')) {
            fallbackRefreshAndOpen(cart);
          }
        }, DRAWER_FALLBACK_DELAY_MS);
        return;
      } catch (error) {
        // The Cart constructor may not have completed on an unusually early action.
      }
    }

    if (attempt < 20) {
      window.setTimeout(function () {
        invokePursuitCart(cart, attempt + 1);
      }, 100);
      return;
    }

    fallbackRefreshAndOpen(cart);
  }

  function triggerPursuitRefresh(cart) {
    var currentTime = now();
    if (currentTime - state.lastPursuitRefreshAt < 300) return;
    state.lastPursuitRefreshAt = currentTime;

    updateHeaderFromCart(cart);

    // Let Seeker finish its own promise callbacks first, then hand off to Pursuit.
    window.setTimeout(function () {
      invokePursuitCart(cart, 0);
    }, 0);
  }

  function handleSuccess(cart) {
    finishTrackedRequest();
    setControlFeedback('success');
    showFeedback('success', 'Added to cart', 2200);
    restoreControl(2200);
    triggerPursuitRefresh(cart);
  }

  function handleFailure(message) {
    finishTrackedRequest();
    setControlFeedback('error');
    showFeedback('error', message || 'We could not add that item. Please try again.', 5500);
    restoreControl(3500);
  }

  function parseFetchCart(response) {
    if (!response || typeof response.clone !== 'function') return Promise.resolve(null);

    return response.clone().json().then(function (payload) {
      return payload && typeof payload.item_count !== 'undefined' ? payload : null;
    }).catch(function () {
      return null;
    });
  }

  function parseFetchError(response) {
    if (!response || typeof response.clone !== 'function') return Promise.resolve('');

    return response.clone().json()
      .then(normaliseErrorMessage)
      .catch(function () {
        return response.clone().text().then(function (text) {
          return normaliseErrorMessage(text);
        }).catch(function () {
          return '';
        });
      });
  }

  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = getRequestUrl(input);
      var method = getRequestMethod(input, init);
      var body = getRequestBody(input, init);
      var tracked = shouldTrackRequest(url, method, body);

      if (tracked) beginTrackedRequest();

      var requestPromise;
      try {
        requestPromise = nativeFetch.apply(window, arguments);
      } catch (error) {
        if (tracked) handleFailure(error && error.message);
        throw error;
      }

      if (!tracked) return requestPromise;

      return requestPromise.then(function (response) {
        if (response && response.ok) {
          parseFetchCart(response).then(handleSuccess);
        } else {
          parseFetchError(response).then(function (message) {
            handleFailure(message);
          });
        }
        return response;
      }, function (error) {
        handleFailure(error && error.message);
        throw error;
      });
    };
  }

  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
    var originalOpen = window.XMLHttpRequest.prototype.open;
    var originalSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__cyclespotSeekerCartRequest = {
        method: String(method || 'GET').toUpperCase(),
        url: url
      };
      return originalOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function (body) {
      var requestInfo = this.__cyclespotSeekerCartRequest || {};
      var tracked = shouldTrackRequest(requestInfo.url, requestInfo.method, body);

      if (tracked) {
        beginTrackedRequest();

        var xhr = this;
        var settled = false;
        var settle = function (successful, message) {
          if (settled) return;
          settled = true;

          if (successful) {
            var cart = null;
            try {
              cart = xhr.responseType === 'json' ? xhr.response : JSON.parse(xhr.responseText || 'null');
            } catch (error) {
              cart = null;
            }
            handleSuccess(cart && typeof cart.item_count !== 'undefined' ? cart : null);
          } else {
            var payload = null;
            try {
              payload = xhr.responseType === 'json' ? xhr.response : JSON.parse(xhr.responseText || 'null');
            } catch (error) {
              payload = null;
            }
            handleFailure(normaliseErrorMessage(payload) || message);
          }
        };

        this.addEventListener('load', function () {
          settle(xhr.status >= 200 && xhr.status < 300, 'We could not add that item. Please try again.');
        }, { once: true });

        this.addEventListener('error', function () {
          settle(false, 'A network error stopped the item being added. Please try again.');
        }, { once: true });

        this.addEventListener('abort', function () {
          settle(false, 'The add-to-cart request was cancelled. Please try again.');
        }, { once: true });

        this.addEventListener('timeout', function () {
          settle(false, 'The add-to-cart request timed out. Please try again.');
        }, { once: true });
      }

      return originalSend.apply(this, arguments);
    };
  }

  injectFeedbackStyles();

  window.CyclespotSeekerCartBridge = {
    version: '1.0.0',
    refresh: function () {
      triggerPursuitRefresh(null);
    }
  };
})();